/**
 * PendingTransactionCard - iter 971g compact redesign
 *
 * Full parity with TransactionTable (pending mode) in a compact card.
 * Everything the table can do, the card can do - without becoming huge.
 *
 * Layout (≈130-180px tall):
 *   Row 1 - [☐] [dir] +Amount · Date · Bank · [status badges]
 *   Row 2 - Counterparty · Description (click to edit)
 *   Row 3 - [Category ▾] · [VAT] [WHT] · [Linked doc ✕]
 *   Row 4 - (only if suspected transfer) [Bank Transfer → picker Confirm]
 *   Row 5 - [Re-classify] [Approve] [⋯]
 *
 * More menu: Split · Make Rule · View · Unlink
 */
import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowDownLeft, ArrowUpRight, CheckCircle2, ThumbsUp, Edit3, Scissors, Landmark,
  Brain, Eye, MoreHorizontal, Link2, Unlink, Sparkles, Target,
  Zap, Info, Check, X, Loader2, ArrowRightLeft, ChevronDown,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { humanizeReviewReason } from './reviewReason';
import { ConfirmationProgressChip } from './ConfirmationProgressChip';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';
import { TaxRateSelect } from './TaxRateSelect';
import { CounterpartyTaxFix } from './CounterpartyTaxFix';
import { pickCategoryDisplay } from './bankFeedDisplay';
import { GlAccountInlineSelect } from './GlAccountInlineSelect';
import { InlineTextEditor } from './InlineTextEditor';
import { ProjectSelect } from '../projects/ProjectSelect';
import { BftAttachmentsButton } from './BftAttachmentsButton';
import { LandedCostPrompt } from '../inventory/LandedCostPrompt';
import { TransferPairChip, TransferFindOtherSideChip, ExtractionWarningBadge, FeeSuggestionChip, StripeHoldChip } from './StatementIntegrityChips';
import { ChannelChip } from './ChannelChip';
import { EditableFieldBox } from './EditableFieldBox';
import { LinkedDocChip } from './LinkedDocChip';
import { User, MessageSquare } from 'lucide-react';

const isBankSideName = (n) => {
  const s = String(n || '').toLowerCase().trim();
  return /^bank account\b/.test(s) || /^bank\s*[-–-]/.test(s)
    || /^cash\b/.test(s) || /^petty cash\b/.test(s)
    || s === 'cash and cash equivalents';
};

// Plain-text bank label: "<Bank Name> ***1234" (no Badge JSX)
const bankPlainText = (bankAccounts, bankAccountId) => {
  if (!bankAccountId || !Array.isArray(bankAccounts)) return '';
  const acct = bankAccounts.find((b) => b.id === bankAccountId);
  if (!acct) return '';
  const name = acct.bank_name || acct.account_name || 'Bank';
  const last4 = acct.account_number ? `***${String(acct.account_number).slice(-4)}` : '';
  return last4 ? `${name} ${last4}` : name;
};

const looksLikeTransfer = (item) => {
  if (item?.status === 'completed') return false;
  // iter1301 - only the strict transfer ENGINE verdict may surface the
  // inter-bank transfer CTA. Raw AI sub_category (e.g. cached transfer_out)
  // previously mis-tagged real vendor payments (HENATOS/ALAWIYE).
  if (item?.suggested_destination_bank_account_id) return true;
  const cls = String(item?.classification || '').toLowerCase();
  return cls === 'internal_transfer';
};

const CategoryPill = ({ item, glAccounts, onChange, saving, pendingEdit }) => {
  const cat = pickCategoryDisplay(item) || {};
  const baseLabel = (cat.name && cat.name !== 'Uncategorised' && !isBankSideName(cat.name))
    ? cat.name
    : (item.ai_gl_credit_name && !isBankSideName(item.ai_gl_credit_name) ? item.ai_gl_credit_name
      : (item.ai_gl_debit_name && !isBankSideName(item.ai_gl_debit_name) ? item.ai_gl_debit_name : null));
  // Jul 16 fix - reflect the user's STAGED edit in the card (previously the
  // card kept showing the old GL after a change, so users thought nothing
  // happened). Same behaviour as the table's pendingGlEdits display.
  const label = pendingEdit ? pendingEdit.glName : baseLabel;
  const code = pendingEdit ? pendingEdit.glCode : (cat.code || item.ai_gl_debit || item.ai_gl_credit);
  const isNeedsCategory = !label;

  return (
    <div className="flex flex-col gap-0.5 min-w-0" data-testid={`card-category-${item.id}`}>
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">Category:</span>
        <GlAccountInlineSelect
          value={code}
          valueName={label || 'Choose a category…'}
          accounts={glAccounts || []}
          onChange={onChange}
          saving={saving}
          direction={item.direction}
          edited={!!pendingEdit}
          testId={`card-gl-select-${item.id}`}
        />
        {isNeedsCategory && (
          <span className="text-[10px] text-amber-700 shrink-0" title="This row still needs a category">•</span>
        )}
      </div>
      {pendingEdit ? (
        <span
          className="text-[9px] font-medium text-amber-700 uppercase tracking-wide pl-1"
          data-testid={`card-gl-pending-${item.id}`}
          title="Your category change will be applied when you click Approve"
        >
          • Will apply on Approve
        </span>
      ) : (
        <span
          className="hidden sm:inline text-[10px] text-muted-foreground italic pl-1"
          data-testid={`card-category-hint-${item.id}`}
        >
          Click to change category
        </span>
      )}
    </div>
  );
};

const DescriptionEditor = ({ item, onSave }) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(item.description || '');
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { setVal(item.description || ''); }, [item.description]);

  const commit = async () => {
    if (val === (item.description || '')) { setOpen(false); return; }
    setSaving(true);
    try { await onSave?.(item, val); setOpen(false); } finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group inline-flex items-center gap-1 max-w-full text-left text-xs text-muted-foreground hover:text-foreground
            rounded px-0.5 -mx-0.5 border-b border-dashed border-slate-300 hover:border-[#108981] hover:bg-[#108981]/5 transition-colors"
          title="Click to edit description"
          data-testid={`card-desc-edit-${item.id}`}
        >
          <span className="truncate italic">
            {item.description
              ? `“${item.description.length > 80 ? item.description.slice(0, 80) + '…' : item.description}”`
              : 'Add a description…'}
          </span>
          <Edit3 className="w-3 h-3 shrink-0 text-slate-400 group-hover:text-[#108981]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" side="bottom" align="start">
        <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Edit description</p>
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="h-8 text-xs"
          autoFocus
          data-testid={`card-desc-input-${item.id}`}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setOpen(false); }}
        />
        <div className="flex justify-end gap-1 mt-2">
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setOpen(false)} disabled={saving}>
            <X className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" className="h-7 px-3" onClick={commit} disabled={saving}
            data-testid={`card-desc-save-${item.id}`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};


export const PendingTransactionCard = React.memo(({
  item,
  index = 0,
  isSelected = false,
  isApproving = false,
  isNew = false,
  onSelect,
  onQuickApprove,
  onReclassify,
  onSplit,
  onTaxPayment,
  onLink,
  onUnlinkDocument,
  onMakeRule,
  onViewDetails,
  onChangeGl,           // (tx, {code, name}) → staged, applied on Approve
  onChangeProject,      // (tx, projectId|null) → saved immediately
  onSaveDescription,    // (tx, newText) → posted immediately
  onSaveCounterparty,   // (tx, newName) → contact name fix, posted immediately
  onRequestAiSuggestion, // (tx, newText) → AI GL suggestion after desc edit
  onApplyAiSuggestion,   // (tx, suggestion) → stage the suggested GL
  pendingGlEdits = null, // Map<txId, {glCode, glName, ...}>: staged edits
  formatAmount,
  getBankLabel,
  currency = 'NGN',
  taxToggle = null,
  // Feb 2026 - flattened tax props (previously nested in `taxToggle`, which
  // created a fresh object each parent render and cascaded into re-render
  // storms when >~40 cards were mounted at once).
  taxCtx = null,
  taxStatusForItem = undefined,
  onFetchTaxStatus = null,
  onToggleVat: onToggleVatProp = null,
  onToggleWht: onToggleWhtProp = null,
  isTogglingTax = false,
  onTaxSaved = null,
  glAccounts = [],
  bankAccounts = [],
  onAfterTransfer = null,
}) => {
  const isInflow = item.direction === 'inflow';
  // iter1310 - always format in the transaction's own bank currency: the
  // review list mixes accounts (GBP/EUR/NGN), so a page-level currency
  // fallback showed the base currency symbol on foreign-bank rows.
  const txCurrency = item.currency || item.bank_currency
    || bankAccounts.find((b) => b.id === item.bank_account_id)?.currency
    || currency;
  const confidence = item.confidence || item.ai_confidence || 0;
  const [savingGl, setSavingGl] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiApplying, setAiApplying] = useState(false);
  const showTransferCta = useMemo(() => looksLikeTransfer(item), [item]);
  const pendingEdit = pendingGlEdits?.get?.(item.id) || null;

  // Feb 2026 - collapse the legacy `taxToggle` bundle OR the new flat props
  // into a single stable object *inside* the memoised card. Callers that
  // still pass `taxToggle` continue to work; new callers pass primitives.
  const resolvedTaxToggle = useMemo(() => {
    if (taxToggle) return taxToggle;
    if (!taxCtx && !onFetchTaxStatus && !onToggleVatProp && !onToggleWhtProp) return null;
    return {
      taxCtx,
      taxStatus: taxStatusForItem,
      onFetchStatus: onFetchTaxStatus,
      onToggleVat: onToggleVatProp,
      onToggleWht: onToggleWhtProp,
      isToggling: isTogglingTax,
      onSaved: onTaxSaved,
    };
  }, [taxToggle, taxCtx, taxStatusForItem, onFetchTaxStatus, onToggleVatProp, onToggleWhtProp, isTogglingTax, onTaxSaved]);

  const handleGlChange = async (account) => {
    if (!onChangeGl) return;
    setSavingGl(true);
    try { await onChangeGl(item, account); } finally { setSavingGl(false); }
  };

  // Jul 16 - parity with the table: after a description save, ask the AI to
  // re-suggest the GL and surface it as an applyable pill on the card.
  const handleDescSave = async (tx, text) => {
    await onSaveDescription?.(tx, text);
    if (onRequestAiSuggestion) {
      setAiLoading(true);
      // fire-and-forget so the editor closes as soon as the save lands
      (async () => {
        try {
          const s = await onRequestAiSuggestion(tx, text);
          setAiSuggestion(s && s.is_different_from_current && s.suggested_gl_code ? s : null);
        } catch { setAiSuggestion(null); } finally { setAiLoading(false); }
      })();
    }
  };

  const handleApplySuggestion = async () => {
    if (!aiSuggestion || !onApplyAiSuggestion) return;
    setAiApplying(true);
    try { await onApplyAiSuggestion(item, aiSuggestion); setAiSuggestion(null); }
    finally { setAiApplying(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: (index || 0) * 0.02 }}
    >
      <Card
        className={`hover:shadow-md transition-all overflow-hidden ${isSelected ? 'ring-2 ring-[#108981] ring-offset-1' : ''}`}
        data-testid={`pending-item-${item.id}`}
      >
        {/* thin colour bar */}
        <div className={`h-0.5 ${isInflow ? 'bg-green-500' : 'bg-red-500'}`} />

        <CardContent className="p-2.5 sm:p-3 space-y-1.5 sm:space-y-2">
          {/* Row 1 — Money line (mobile: never wraps). NEW badge and status
              stay here; SYNC / confidence / Auto badges have moved down to
              the meta strip so this line stays scannable. */}
          <div className="flex items-center gap-2 min-w-0">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onSelect?.(item.id)}
              data-testid={`checkbox-${item.id}`}
            />
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${isInflow ? 'bg-green-100' : 'bg-red-100'}`}>
              {isInflow
                ? <ArrowDownLeft className="w-3.5 h-3.5 text-green-600" />
                : <ArrowUpRight className="w-3.5 h-3.5 text-red-600" />}
            </div>
            <span className={`text-sm sm:text-base font-semibold whitespace-nowrap ${isInflow ? 'text-green-600' : 'text-red-600'}`}>
              {isInflow ? '+' : '-'}{formatAmount?.(item.amount, txCurrency) || item.amount?.toLocaleString()}
            </span>
            <span className="text-[11px] sm:text-xs text-muted-foreground whitespace-nowrap">· {item.date}</span>
            {(() => {
              const bt = bankPlainText(bankAccounts, item.bank_account_id);
              if (!bt) return null;
              return (
                <span
                  className="hidden sm:inline text-[11px] sm:text-xs text-muted-foreground whitespace-nowrap truncate max-w-[180px]"
                  title={bt}
                  data-testid={`card-bank-label-${item.id}`}
                >
                  · {bt}
                </span>
              );
            })()}
            <div className="ml-auto flex items-center gap-1 shrink-0">
              {isNew && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500 text-white leading-none">NEW</span>
              )}
            </div>
          </div>
          {/* Bank label on its own row on mobile (moved out of Row 1 so it never wraps a badge column) */}
          {(() => {
            const bt = bankPlainText(bankAccounts, item.bank_account_id);
            if (!bt) return null;
            return (
              <div
                className="sm:hidden text-[11px] text-muted-foreground truncate pl-8 -mt-1"
                title={bt}
                data-testid={`card-bank-label-mobile-${item.id}`}
              >
                {bt}
              </div>
            );
          })()}

          {/* iter1040 Fix#4 - plain-words reason on every review card */}
          {(item.review_reason || item.processing_reasoning || item.ai_suggestion || item.ai_reasoning) && (
            <div
              className="flex items-start gap-1.5 text-[11px] sm:text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2 py-1"
              data-testid={`why-review-${item.id}`}
            >
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-500" />
              <span className="min-w-0">
                <span className="font-semibold">Why this needs you: </span>
                {item.review_reason || humanizeReviewReason(item.processing_reasoning || item.ai_suggestion || item.ai_reasoning)}
              </span>
            </div>
          )}
          {item.confirmation_progress && (
            <div className="-mt-1"><ConfirmationProgressChip item={item} /></div>
          )}

          {/* Row 2 — Counterparty + Description in bordered edit boxes.
              Foreground-weight text, always-visible pencil, hover-ring in
              brand teal. Stack vertically on mobile so long text is fully
              readable; side-by-side on ≥sm for compactness. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 sm:pl-8">
            <EditableFieldBox
              icon={User}
              value={item.ai_counterparty || item.counterparty || ''}
              placeholder="Add contact name…"
              label="contact name"
              onSave={(next) => onSaveCounterparty?.(item, next)}
              testIdPrefix={`card-contact-${item.id}`}
            />
            <EditableFieldBox
              icon={MessageSquare}
              value={item.description || ''}
              placeholder="Add a description…"
              label="description"
              multiline
              onSave={(next) => handleDescSave(item, next)}
              testIdPrefix={`card-desc-${item.id}`}
            />
          </div>

          {/* AI GL suggestion after a description edit (Jul 16 - card parity with table) */}
          {aiLoading && (
            <div className="flex items-center gap-1 sm:pl-8 text-[10px] text-violet-600" data-testid={`card-ai-loading-${item.id}`}>
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
              AI is re-suggesting a category…
            </div>
          )}
          {aiSuggestion && !aiLoading && (
            <div
              className="flex items-center gap-1.5 sm:ml-8 px-2 py-1.5 rounded-md bg-violet-50 border border-violet-200 text-[11px]"
              data-testid={`card-ai-suggestion-${item.id}`}
            >
              <Sparkles className="w-3 h-3 text-violet-600 shrink-0" />
              <span className="text-violet-800 min-w-0 truncate">
                AI suggests: <b>{aiSuggestion.suggested_gl_name}</b>
                {aiSuggestion.confidence > 0 && (
                  <span className="text-violet-500 ml-1">· {Math.round((aiSuggestion.confidence || 0) * 100)}%</span>
                )}
              </span>
              <button
                type="button"
                onClick={handleApplySuggestion}
                disabled={aiApplying || !onApplyAiSuggestion}
                className="ml-auto h-5 px-2 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium text-[10px] disabled:opacity-60 shrink-0"
                data-testid={`card-ai-apply-${item.id}`}
              >
                {aiApplying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
              </button>
              <button
                type="button"
                onClick={() => setAiSuggestion(null)}
                className="h-5 w-5 rounded text-violet-700 hover:bg-violet-100 grid place-items-center shrink-0"
                data-testid={`card-ai-dismiss-${item.id}`}
                aria-label="Dismiss suggestion"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Row 3 - category · tax · linked doc */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap min-w-0 sm:pl-8">
            {showTransferCta ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-sky-700"
                data-testid={`card-transfer-hint-${item.id}`}
              >
                <ArrowRightLeft className="w-3 h-3" />
                Categorised as inter-bank transfer
              </span>
            ) : (
              <CategoryPill
                item={item}
                glAccounts={glAccounts}
                onChange={handleGlChange}
                saving={savingGl}
                pendingEdit={pendingEdit}
              />
            )}
            <ProjectSelect
              compact
              value={item.project_id || null}
              onChange={(pid) => onChangeProject?.(item, pid)}
              createdFrom="bank_feed"
              autoMatched={!!item.project_auto_matched}
            />
            {resolvedTaxToggle && (
              <>
                {resolvedTaxToggle.taxCtx?.show_vat && (
                  <TaxRateSelect
                    kind="vat"
                    transaction={item}
                    taxCtx={resolvedTaxToggle.taxCtx}
                    taxStatus={resolvedTaxToggle.taxStatus}
                    onFetchStatus={resolvedTaxToggle.onFetchStatus}
                    onSaved={resolvedTaxToggle.onSaved}
                  />
                )}
                {resolvedTaxToggle.taxCtx?.show_wht && (
                  <TaxRateSelect
                    kind="wht"
                    transaction={item}
                    taxCtx={resolvedTaxToggle.taxCtx}
                    taxStatus={resolvedTaxToggle.taxStatus}
                    onFetchStatus={resolvedTaxToggle.onFetchStatus}
                    onSaved={resolvedTaxToggle.onSaved}
                  />
                )}
                <CounterpartyTaxFix item={item} taxStatus={resolvedTaxToggle.taxStatus} compact
                  onChanged={(txId, fresh) => { if (resolvedTaxToggle.onFetchStatus) resolvedTaxToggle.onFetchStatus(txId, true); if (resolvedTaxToggle.onSaved) resolvedTaxToggle.onSaved(txId, fresh); }} />
              </>
            )}
            <LinkedDocChip item={item} onUnlink={onUnlinkDocument} txCurrency={txCurrency} />
            <BftAttachmentsButton bftId={item.id} compact />
          </div>

          {/* iter 996/998 - landed-cost prompt + transfer/extraction chips */}
          <div className="sm:pl-8 flex flex-wrap items-center gap-1.5 empty:hidden">
            <TransferPairChip tx={item} onDone={onAfterTransfer} />
            <TransferFindOtherSideChip tx={item} onDone={onAfterTransfer} />
            <StripeHoldChip tx={item} />
            <FeeSuggestionChip tx={item} />
            <ExtractionWarningBadge tx={item} />
            <LandedCostPrompt tx={item} onDone={onAfterTransfer} />
          </div>

          {/* Meta strip — demoted low-emphasis metadata (channel, AI confidence,
              auto-discovery). Single line, muted, no pills. Was previously
              pilled up in Row 1 and caused staircase wrapping on mobile. */}
          {(item.channel || item.source || confidence > 0 || item.auto_discovered || item.auto_approved) && (
            <div
              className="sm:pl-8 flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap"
              data-testid={`card-meta-strip-${item.id}`}
            >
              <ChannelChip item={item} size="xs" />
              {confidence > 0 && (
                <span className={`inline-flex items-center gap-0.5 ${
                  confidence >= 0.80 ? 'text-emerald-700'
                  : confidence >= 0.60 ? 'text-blue-700'
                  : 'text-amber-700'
                }`}>
                  <Sparkles className="w-2.5 h-2.5" />
                  {Math.round(confidence * 100)}% AI
                </span>
              )}
              {item.auto_discovered && (
                <span className="inline-flex items-center gap-0.5 text-blue-700">
                  <Target className="w-2.5 h-2.5" /> Auto-matched
                </span>
              )}
              {item.auto_approved && (
                <span className="inline-flex items-center gap-0.5 text-emerald-700">
                  <Zap className="w-2.5 h-2.5" /> Auto-approved
                </span>
              )}
            </div>
          )}

          {/* Row 4 - (Oct 2026) the one-sided "Inter-Bank Transfer → Confirm" strip was retired:
              transfers are only recorded through the paired review dialog above. */}

          {/* Row 5 - primary CTAs + visible "More" menu */}
          <div className="flex items-center gap-1 sm:pl-8 pt-0.5 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-[11px] sm:text-xs gap-1"
              onClick={() => onReclassify?.(item)}
              data-testid={`card-reclassify-${item.id}`}
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Re-classify</span>
            </Button>

            {onLink && !item.matched_document_ref && !item.matched_document_id && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[11px] sm:text-xs gap-1 border-blue-200 text-blue-700 hover:bg-blue-50"
                onClick={() => onLink(item)}
                data-testid={`card-link-doc-${item.id}`}
                title="Match this bank line to an invoice, bill, loan or asset you already recorded"
              >
                <Link2 className="w-3.5 h-3.5" />
                <span>Link existing transaction</span>
              </Button>
            )}

            <Button
              size="sm"
              className="h-7 px-3 text-[11px] sm:text-xs gap-1 bg-[#108981] hover:bg-[#0d6f68] text-white ml-auto"
              onClick={() => onQuickApprove?.(item)}
              disabled={isApproving}
              data-testid={`card-approve-${item.id}`}
            >
              {isApproving
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <ThumbsUp className="w-3.5 h-3.5" />}
              <span>Approve</span>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px] sm:text-xs gap-1 border-slate-300 text-slate-700 hover:bg-slate-100 font-medium"
                  aria-label="More actions"
                  title="More actions: Split, Make a rule, View details"
                  data-testid={`card-more-${item.id}`}
                >
                  <MoreHorizontal className="w-4 h-4" />
                  <span>More</span>
                  <ChevronDown className="w-3 h-3 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {onSplit && (
                  <DropdownMenuItem onSelect={() => onSplit(item)} className="gap-2 text-xs"
                    data-testid={`card-split-${item.id}`}>
                    <Scissors className="w-3.5 h-3.5 text-purple-600" />Split transaction
                  </DropdownMenuItem>
                )}
                {onTaxPayment && !isInflow && (
                  <DropdownMenuItem onSelect={() => onTaxPayment(item)} className="gap-2 text-xs"
                    data-testid={`card-tax-payment-${item.id}`}>
                    <Landmark className="w-3.5 h-3.5 text-[#283F8B]" />Record income tax payment
                  </DropdownMenuItem>
                )}
                {onMakeRule && (
                  <DropdownMenuItem onSelect={() => onMakeRule(item)} className="gap-2 text-xs"
                    data-testid={`card-make-rule-${item.id}`}>
                    <Brain className="w-3.5 h-3.5 text-emerald-600" />Make this a rule
                  </DropdownMenuItem>
                )}
                {onUnlinkDocument && (item.matched_document_ref || item.matched_document_id) && (
                  <DropdownMenuItem onSelect={() => onUnlinkDocument(item)} className="gap-2 text-xs">
                    <Unlink className="w-3.5 h-3.5 text-red-600" />Unlink document
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {onViewDetails && (
                  <DropdownMenuItem onSelect={() => onViewDetails(item)} className="gap-2 text-xs"
                    data-testid={`card-view-${item.id}`}>
                    <Eye className="w-3.5 h-3.5" />View details
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
});
PendingTransactionCard.displayName = 'PendingTransactionCard';

export default PendingTransactionCard;
